import { useState } from 'react'
import { ShieldAlert, Trash2, RefreshCw, RotateCcw, Search, Download } from 'lucide-react'
import { useQuarantine } from './useFiles'
import { formatDate } from './formatters'
import { filesApi } from '@papan/files/client'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm } from '@papan/core-ui/ui'
import { damagedMessageFrom } from '@papan/types'

// Show dev tools only in dev mode
const isDev = import.meta.env.DEV

/** #1102 item 2 — no role here: the server lists only the items the person may act on (Manage where each one
 *  belongs), so every listed row's actions are theirs. */
export function QuarantineView() {
  const { t } = useTranslation('files')
  const toast = useToast()
  const confirm = useConfirm()
  const { items, loading, refresh, deleteItem, restoreItem, downloadItem } = useQuarantine()
  const [isScanning, setIsScanning] = useState(false)

  const handleDelete = async (itemId: string) => {
    const confirmed = await confirm({
      title: t('quarantineView.confirm.deleteTitle'),
      message: t('quarantineView.confirm.deletePermanently'),
      type: 'danger',
      confirmText: t('quarantineView.actions.deletePermanently'),
    })
    if (confirmed) {
      try {
        await deleteItem(itemId)
        toast.success(t('quarantineView.toast.deleted'))
      } catch {
        toast.error(t('quarantineView.toast.deleteFailed'))
      }
    }
  }

  const handleDownload = async (itemId: string) => {
    try {
      await downloadItem(itemId)
      toast.warning(t('quarantineView.toast.downloadWarning'))
    } catch {
      toast.error(t('quarantineView.toast.downloadFailed'))
    }
  }

  const handleRestore = async (item: { id: string; threat: string; filename: string }) => {
    const confirmed = await confirm({
      title: t('quarantineView.confirm.restoreTitle'),
      message: t('quarantineView.confirm.restoreWarning', { threat: item.threat, path: item.filename }),
      type: 'danger',
      confirmText: t('quarantineView.actions.restoreAnyway'),
    })
    if (confirmed) {
      try {
        const result = await restoreItem(item.id)
        toast.success(t('quarantineView.toast.restored', { path: result.restoredName }))
      } catch (err) {
        // #1397: a damaged stored copy is refused in the server's words (nothing was restored, the item stays here)
        toast.error(damagedMessageFrom(err) ?? t('quarantineView.toast.restoreFailed'))
      }
    }
  }

  return (
    <div className="flex-1 min-h-0 themed-bg-primary flex flex-col">
      {/* Header */}
      <div className="h-12 border-b themed-border flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <ShieldAlert size={20} className="text-red-600 dark:text-red-400" />
          <span className="themed-text font-medium">{t('quarantineView.title')}</span>
          <span className="themed-text-muted text-sm">{t('quarantineView.itemCount', { count: items.length })}</span>
        </div>
        <div className="flex items-center gap-2">
          {isDev && (
            <button
              onClick={async () => {
                setIsScanning(true)
                try {
                  const result = await filesApi.triggerRescan(30)
                  toast.success(`Rescan: ${result.scanned} scanned, ${result.infected} infected`)
                  refresh()
                } catch (err) {
                  toast.error('Rescan failed')
                } finally {
                  setIsScanning(false)
                }
              }}
              disabled={isScanning}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-800 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors"
              title="Run malware rescan (dev only)"
            >
              <Search size={16} className={isScanning ? 'animate-pulse' : ''} />
              {isScanning ? 'Scanning...' : 'Rescan'}
            </button>
          )}
          <button
            onClick={refresh}
            className="p-2 themed-icon hover:themed-icon-active themed-row-hover rounded-md transition-colors"
            title={t('common:refresh')}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Warning banner — an ALARM only when something is actually here.
          It used to render red, with its heading, unconditionally: an empty
          quarantine announced "These files contain malware" in red directly
          above a green "No threats detected. All files are clean." The screen
          contradicted itself, and the louder half was the wrong one.
          The explanation stays either way — someone opening an empty
          quarantine still deserves to know what the page is for — but with
          nothing to warn about it is muted text and no heading. */}
      <div className="px-4 py-3 border-b themed-border themed-bg-secondary text-sm">
        <div className={`flex items-start gap-2 ${items.length > 0 ? 'text-red-500 dark:text-red-400' : 'themed-text-muted'}`}>
          <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            {items.length > 0 && <p className="font-medium">{t('quarantineView.warning.title')}</p>}
            <p className={items.length > 0 ? 'opacity-80 text-xs mt-1' : 'text-xs'}>
              {t(items.length > 0 ? 'quarantineView.warning.description' : 'quarantineView.warning.emptyDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Quarantine list header */}
      <div className="h-10 border-b themed-border flex items-center px-4 text-xs themed-text-muted uppercase tracking-wide">
        <div className="flex-1">{t('quarantineView.header.originalPath')}</div>
        <div className="w-48">{t('quarantineView.header.threat')}</div>
        <div className="w-40">{t('quarantineView.header.quarantinedAt')}</div>
        <div className="w-20"></div>
      </div>

      {/* Quarantine list */}
      <div className="flex-1 overflow-auto">
        {loading && items.length === 0 ? (
          <div className="p-4 themed-text-muted text-sm">{t('quarantineView.status.loading')}</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center themed-text-muted">
            <ShieldAlert size={48} className="mx-auto mb-4 opacity-50 text-green-600 dark:text-green-500" />
            <p className="text-green-600 dark:text-green-400">{t('quarantineView.status.empty')}</p>
            <p className="text-sm mt-2 themed-text-muted">{t('quarantineView.status.emptyDescription')}</p>
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="h-14 flex items-center px-4 border-b themed-row-border themed-row-hover transition-colors group"
            >
              <div className="flex-1 themed-text truncate">
                <div className="truncate">{item.filename}</div>
              </div>
              <div className="w-48 text-sm text-red-600 dark:text-red-400 font-medium">{item.threat}</div>
              <div className="w-40 text-sm themed-text-muted">{formatDate(item.quarantinedAt)}</div>
              <div className="w-24 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleDownload(item.id)}
                  className="p-1.5 themed-icon hover:text-blue-600 dark:hover:text-blue-400 hover:themed-bg-tertiary rounded"
                  title={t('quarantineView.actions.download')}
                >
                  <Download size={16} />
                </button>
                {isDev && (
                  <button
                    onClick={() => handleRestore(item)}
                    className="p-1.5 themed-icon hover:text-amber-600 dark:hover:text-amber-400 hover:themed-bg-tertiary rounded"
                    title={t('quarantineView.actions.restore')}
                  >
                    <RotateCcw size={16} />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-1.5 themed-icon hover:text-red-600 dark:hover:text-red-400 hover:themed-bg-tertiary rounded"
                  title={t('quarantineView.actions.deletePermanently')}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Status bar */}
      <div className="h-8 border-t themed-border flex items-center px-4 text-xs themed-text-muted">
        <span>{t('quarantineView.status.items', { count: items.length })}</span>
      </div>
    </div>
  )
}
